import { useCallback, useEffect, useMemo, useState } from 'react';

import ReplenishmentViewController from '~views/ShoppingBagTab/CartSidebarItems/CartItem/ReplenishmentComponent/ReplenishmentViewController';

import ICartItem from '~domain/entities/CartItem/ICartItem';

export const REPLENISHMENT_BUY_ONCE = '0';
const REPLENISHMENT_DEFAULT_PERIOD = '30';

export const useReplenishment = (controller: ReplenishmentViewController, cartItem: ICartItem) => {
    const {
        selectedValue,
        isLoading,
        getReplenishmentFrequency,
        updateReplenishment,
        setSelected
    } = controller;
    const { replenishmentOptions = [] } = cartItem;

    const defaultOption = useMemo(
        () => replenishmentOptions.find((option) => option.is_chosen) ?? replenishmentOptions?.[0],
        [replenishmentOptions]
    );
    const defaultValue = useMemo(() => defaultOption?.value ?? REPLENISHMENT_BUY_ONCE, [
        defaultOption
    ]);

    const isAlreadySubscribed = useMemo(() => !!Number(defaultValue), [defaultValue]);
    const [subscribe, setSubscribe] = useState(isAlreadySubscribed);

    const defaultOptionWithoutBuyOnceValue = useMemo(
        () =>
            replenishmentOptions.find(
                ({ value }) => Number(value) !== Number(REPLENISHMENT_BUY_ONCE)
            )?.value ?? REPLENISHMENT_DEFAULT_PERIOD,
        [replenishmentOptions]
    );
    const initDefaultSelection = useMemo(
        () => (isAlreadySubscribed ? defaultValue : defaultOptionWithoutBuyOnceValue),
        [isAlreadySubscribed, defaultValue, defaultOptionWithoutBuyOnceValue]
    );

    const fetchCollectionInfo = useCallback(() => {
        setSelected(defaultValue);
        const collectionId = cartItem.replenishmentInfo?.collectionId?.toString();
        if (collectionId) {
            getReplenishmentFrequency({ collectionId });
        }
    }, [cartItem, defaultValue]);

    const onChange = useCallback(
        (selectedFrequency: string) => updateReplenishment({ cartItem, selectedFrequency }),
        [cartItem]
    );

    const onSubscribeChange = useCallback(
        (isChecked: boolean) => {
            setSubscribe(isChecked);
            if (isChecked) {
                if (selectedValue !== initDefaultSelection) {
                    setSelected(initDefaultSelection);
                }
            } else {
                if (selectedValue !== REPLENISHMENT_BUY_ONCE) {
                    setSelected(REPLENISHMENT_BUY_ONCE);
                }
            }
        },
        [initDefaultSelection, selectedValue]
    );

    useEffect(() => {
        const updateReplenishmentValue = async () => {
            await onChange(selectedValue);
        };

        if (selectedValue && !isLoading) {
            updateReplenishmentValue();
        }
    }, [selectedValue]);

    useEffect(() => {
        fetchCollectionInfo();
    }, []);

    return {
        isLoading,
        selectedValue,
        subscribe,
        onChange,
        onSubscribeChange,
        setSelected
    };
};
